import { Injectable, inject } from '@angular/core';
import { Observable, finalize, tap } from 'rxjs';

import { AuthService } from '@core/services/auth.service';

import { AuthSession } from '../../models/auth-session.model';
import {
  FastLoginChallengeDto,
  FastLoginChannel,
  FastLoginRequestBody,
  FastLoginResendBody,
  FastLoginVerifyBody,
} from '../../models/fast-login.dto';
import { AuthRepositoryContract } from '../repositories/auth.repository.contract';
import { AuthStore } from '../store/auth.store';
import { apiErrorMessage } from './api-error-message';

@Injectable()
export class FastLoginUseCase {
  private readonly repository = inject(AuthRepositoryContract);
  private readonly store = inject(AuthStore);
  private readonly auth = inject(AuthService);

  /**
   * Request OTP via email or WhatsApp.
   * Returns challenge data including maskedDestination and timing info.
   */
  requestOtp(
    channel: FastLoginChannel,
    email?: string,
    whatsAppNumber?: string,
    countryCode?: string,
    nationalNumber?: string,
  ): Observable<FastLoginChallengeDto> {
    // Build request body based on channel.
    // Use empty strings for unused fields - the API may not handle JSON null correctly.
    const body: FastLoginRequestBody = {
      channel,
      email: channel === 'Email' ? (email || '') : '',
      whatsAppNumber: channel === 'WhatsApp' ? (whatsAppNumber || '') : '',
      countryCode: channel === 'WhatsApp' ? (countryCode || '') : '',
      nationalNumber: channel === 'WhatsApp' ? (nationalNumber || '') : '',
    };

    this.store.setLoading(true);
    this.store.setError(null);

    return this.repository.fastLoginRequest(body).pipe(
      tap({
        error: (err: unknown) => {
          this.store.setError(
            apiErrorMessage(err, 'We could not send the code. Please check your details and try again.'),
          );
        },
      }),
      finalize(() => this.store.setLoading(false)),
    );
  }

  /**
   * Resend OTP (invalidates previous code).
   */
  resendOtp(challengeId: string): Observable<FastLoginChallengeDto> {
    const body: FastLoginResendBody = { challengeId };

    this.store.setLoading(true);
    this.store.setError(null);

    return this.repository.fastLoginResend(body).pipe(
      tap({
        error: (err: unknown) => {
          this.store.setError(
            apiErrorMessage(err, 'We could not resend the code. Please try again.'),
          );
        },
      }),
      finalize(() => this.store.setLoading(false)),
    );
  }

  /**
   * Verify OTP and establish session.
   * On success, sets the auth session (same as password login).
   */
  verifyOtp(challengeId: string, code: string): Observable<AuthSession> {
    const body: FastLoginVerifyBody = { challengeId, code };

    this.store.setLoading(true);
    this.store.setError(null);

    return this.repository.fastLoginVerify(body).pipe(
      tap({
        next: (session) => {
          this.auth.setSession(session.token, session.user);
          this.store.setUser(session.user ?? null);
        },
        error: (err: unknown) => {
          this.store.setError(
            apiErrorMessage(err, 'Invalid or expired code. Please try again.'),
          );
        },
      }),
      finalize(() => this.store.setLoading(false)),
    );
  }
}
